import { Stack, Typography } from '@mui/material';
import { GridColDef, GridRow } from '@mui/x-data-grid-pro';

import AppLink from '@/components/AppLink';
import DataGrid from '@/components/DataGrid';
import { BasetenPageEnum } from '@/routes/types';
import { WorkspaceChainFragment } from '@/sections/Overview/Chains/__generated__/fragments.generated';
import { usePinnedAPI } from '@/sections/Overview/Chains/hooks';
import VersionsCountByStatus from '@/sections/Workspace/VersionsCountByStatus';
import { getVersionsByStatus, sortVersionsByStatus } from '@/sections/Workspace/utils';
import { dateDisplay } from '@/utils/date';
import moment from 'moment';

import ProductionStatus from '../../sections/ProductionStatus';
import { dataGridProps, dataGridStyles } from '../../styled';
import Actions from '../Actions';
import { ListProps } from '../types';

const ROW_HEIGHT = 67;

function Table({ chains, height }: ListProps) {
  const { getPinActionItem } = usePinnedAPI();
  const columns: GridColDef<WorkspaceChainFragment>[] = [
    {
      field: 'name',
      headerName: 'Name',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: ({ row }) => {
        const displayTime = dateDisplay(moment(row.lastActivity));
        return (
          <Stack gap={0.5}>
            <Stack gap={0.5} direction="row" alignItems="center">
              <Typography noWrap variant="h5">
                {row.name}
              </Typography>
              <Typography color="text.muted">{row.id}</Typography>
            </Stack>
            <Typography variant="body2" color="text.subtle">
              last activity {displayTime}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Production',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }) => <ProductionStatus primary={row.primaryDeployment} />,
    },
    {
      field: 'versions',
      headerName: 'Deployments',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }) => {
        return (
          <VersionsCountByStatus
            options={sortVersionsByStatus(
              getVersionsByStatus(row.deploymentCountByStatus, undefined, true),
            )}
          />
        );
      },
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 110,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" gap={0.2}>
            {getPinActionItem(row.id, row.name)}
            <Actions chainId={row.id} chainName={row.name} variant="minimal" />
          </Stack>
        );
      },
    },
  ];

  return (
    <Stack data-cy="chain-table-content" height={height}>
      <DataGrid<WorkspaceChainFragment>
        rows={chains}
        columns={columns}
        rowHeight={ROW_HEIGHT}
        getRowId={(row) => row.id}
        disableAutoHeight
        sx={dataGridStyles}
        {...dataGridProps}
        slots={{
          row: (props) => (
            <AppLink
              page={BasetenPageEnum.Chain}
              params={{ chainId: props.row.id }}
              key={props.row}
              underline="none"
              sx={{ color: 'inherit', '&:hover': { textDecoration: 'none' } }}
            >
              <GridRow {...props} />
            </AppLink>
          ),
        }}
      />
    </Stack>
  );
}

export default Table;
